import { FC } from 'react';
import styled, { css } from 'styled-components';
import { usePrevious } from '../hooks';

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  width: 96px;

  * {
    user-select: none;
  }
`;

type InpStyleProps = { hasGuests: boolean; flash: boolean };

const Input = styled.div<InpStyleProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  padding: 0;

  border: none;
  outline: none;
  color: #1c1c1c;
  text-align: center;
  font-size: 12px;
  text-transform: uppercase;

  ${(props) =>
    props.hasGuests &&
    css`
      font-weight: 500;
    `}

  ${(props) =>
    props.flash &&
    css`
      @keyframes ni-flash-reset-anim {
        from {
          color: inherit;
          font-weight: 300;
        }
        to {
          color: #ff9a9a;
          font-weight: 500;
        }
      }

      @keyframes ni-flash-number-bounce {
        0%,
        100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-5px);
        }
      }

      animation: ni-flash-reset-anim 333ms cubic-bezier(0.6, -0.28, 0.735, 0.045) 1111ms 3,
        ni-flash-number-bounce 333ms ease-in-out 1111ms 3;
    `};
`;

type EBStyleProps = { disabled: boolean; flash?: boolean; isFaded?: boolean };

const EditButton = styled.button<EBStyleProps>`
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background-color: #FF6E3D;
  border: none;
  outline: none !important;

  &:active:hover {
    transform: scale(0.9);
    border-radius: 6px;
  }

  > i {
    color: #fff;
    font-size: 16px;
  }

  ${(props) =>
    props.disabled &&
    css`
      pointer-events: none;
      background-color: #e8e8e8;
    `}

  ${(props) =>
    props.flash &&
    css`
      @keyframes ni-flash-reset-anim-bg {
        0% {
          background-color: inherit;
          opacity: inherit;
        }
        30% {
          background-color: #ff9a9a;
          opacity: 1;
        }
        100% {
          background-color: inherit;
          opacity: inherit;
        }
      }

      animation: ni-flash-reset-anim-bg 666ms linear 3;
    `};

  ${(props) =>
    props.isFaded &&
    css`
      opacity: 0.5;
    `}
`;

type OnChangeParams = { type: 'Increment' } | { type: 'Decrement' };

type NumberInputProps = {
  value: number;
  onChange: (value: OnChangeParams) => void;
  minValue?: number;
  description?: string;
  className?: string;
  disabled?: boolean;
  incrementDisabled?: boolean;
  steppersFaded?: boolean;
  doFlashReset?: boolean;
};

const NumberInput: FC<NumberInputProps> = ({
  value,
  onChange,
  minValue = 0,
  description,
  className,
  disabled = false,
  incrementDisabled = false,
  steppersFaded = false,
  doFlashReset = false,
}) => {
  const prevValue: number | undefined = usePrevious(value);
  const shouldDoFlashReset = !!(prevValue && prevValue > 0 && doFlashReset);

  return (
    <InputContainer className={`${className || ''}`}>
      <EditButton
        type="button"
        disabled={value === 0 || disabled}
        onClick={() => {
          if ((value || 0) - 1 >= minValue && !disabled) {
            onChange({ type: 'Decrement' });
          }
        }}
        flash={shouldDoFlashReset}
        isFaded={steppersFaded}
      >
        <i className="material-icons">remove</i>
      </EditButton>
      <Input hasGuests={value > 0} flash={shouldDoFlashReset}>{`${value} ${
        description ? description : ''
      }`}</Input>
      <EditButton
        type="button"
        disabled={disabled || incrementDisabled}
        onClick={() => {
          if (!disabled) {
            onChange({ type: 'Increment' });
          }
        }}
        isFaded={steppersFaded}
      >
        <i className="material-icons">add</i>
      </EditButton>
    </InputContainer>
  );
};

export default NumberInput;
